'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import type { DriveFormValues } from '@eliadmin/core/src/types/schemas/drive-form-schema';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { driveSchema } from '@eliadmin/core/src/types/schemas/drive-form-schema';
import { useMutation } from '@tanstack/react-query';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { FirstStep } from './first-step';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { SecondStep } from './second-step';
import { Congratulations } from './congratulation';

export type Step = 1 | 2 | 3;

export function DriveForm({ products }: { products: Product[] }) {
  const [step, setStep] = useState<Step>(1);
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const locale = useLocaleStore(s => s.currentLocale);

  const [selectedProductName, setSelectedProductName] = useState<string>('');

  const form = useForm<DriveFormValues>({
    resolver: zodResolver(driveSchema(formValidationMessages)),
    defaultValues: {
      custom: {
        name: selectedProductName,
      },
      name: '',
      phone: '',
      email: '',
      text: '',
      gdpr: false,
    },
  });

  const handleStepChange = (newStep: Step) => {
    setStep(newStep);
  };

  React.useEffect(() => {
    form.setValue('custom.name', selectedProductName);
  }, [selectedProductName, form]);

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const handleSubmit = form.handleSubmit(async data => {
    if (step === 2) {
      try {
        await mutateAsync({
          data: {
            ...data,
          },
          locale,
        });

        toast({
          title: t('text658'),
          variant: 'success',
        });
        handleStepChange(3);
      } catch (err) {
        if (err instanceof ValidationError) {
          for (const [field, errors] of Object.entries(err.fields)) {
            form.setError(field as any, { type: 'manual', message: errors.join('\n') });
          }
          return;
        }

        toast({
          title: t('text657'),
          description: (err as any)?.message,
          variant: 'destructive',
        });
      }
    } else if (step === 1) {
      handleStepChange(2);
    }
  });

  function handlePrevStep() {
    handleStepChange(1);
  }

  function handleNextStep() {
    handleStepChange(2);
  }

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
    }),
  };

  const direction = step === 1 ? 0 : step === 2 ? 1 : 2;

  return (
    <Form {...form}>
      <CaptchaFormMessage errors={form.formState.errors} />
      <div className="relative h-[800px] w-full overflow-hidden">
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            className="absolute left-0 top-0 w-full"
          >
            <form onSubmit={handleSubmit} className="pb-24">
              {step === 1 && (
                <FirstStep
                  products={products}
                  onProductSelect={setSelectedProductName}
                  selectedProductName={selectedProductName}
                  onNextStep={handleNextStep}
                />
              )}
              {step === 2 && <SecondStep form={form} onPrevStep={handlePrevStep} />}
              {step === 3 && <Congratulations />}
            </form>
          </motion.div>
        </AnimatePresence>
      </div>
    </Form>
  );
}
